import { useEffect, useState } from 'react'
import { AlertCircle } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card'
import { Label } from '../../../ui/label'
import { FileUpload } from '../../file-upload'

interface OverviewBannerSectionProps {
  localData: {
    banner: File | null
    bannerUrl?: string
  }
  bannerUploading: boolean
  bannerUploadError?: string
  onBannerSelected: (file: File | null) => void
  requestChanges?: Record<string, string>
}

const getRemoteFileUrl = (file: File | null): string => {
  if (!file) return ''
  const serverUrl = (file as File & { serverUrl?: string }).serverUrl
  return typeof serverUrl === 'string' ? serverUrl : ''
}

export function OverviewBannerSection({
  localData,
  bannerUploading,
  bannerUploadError,
  onBannerSelected,
  requestChanges,
}: OverviewBannerSectionProps) {
  const [localPreviewUrl, setLocalPreviewUrl] = useState('')

  useEffect(() => {
    if (!(localData.banner instanceof File) || localData.banner.size === 0) {
      setLocalPreviewUrl('')
      return
    }

    const objectUrl = URL.createObjectURL(localData.banner)
    setLocalPreviewUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [localData.banner])

  const previewUrl =
    localPreviewUrl ||
    getRemoteFileUrl(localData.banner) ||
    localData.bannerUrl ||
    ''

  return (
    <Card>
      <CardHeader>
        <CardTitle>Project Banner</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 md:grid-cols-12">
          <div className="space-y-2 md:col-span-8" id="banner">
            <Label>Banner Upload (optional)</Label>
            <FileUpload
              mode="image"
              accept="image/png"
              buttonLabel="Upload Banner"
              helperText="Upload an 800×192px PNG"
              maxSizeMB={2}
              errorStrings={{
                fileCount: 'Please upload one image.',
                fileType: 'PNG only. Please upload a .png file.',
                fileSize: 'File is too large (max 2 MB).',
                unreadable:
                  "We couldn't read this image. Please try another file.",
                generic: 'Upload failed. Please try again.',
              }}
              ariaLabel="Upload project banner PNG file"
              onFileSelect={onBannerSelected}
              externalUploading={bannerUploading}
              externalError={bannerUploadError}
              currentFile={localData.banner}
              requiredWidth={800}
              requiredHeight={192}
            />
            {requestChanges?.banner && (
              <p className="text-status-error text-xs inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {requestChanges.banner}
              </p>
            )}
          </div>

          <div className="space-y-2 md:col-span-4">
            <Label>Live Preview</Label>
            <div className="w-full h-24 rounded-lg overflow-hidden bg-neutral-100">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Project banner preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">
                  No banner uploaded
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
